import { z } from 'zod';
import { EvidenceTypeSchema, SALES_EVIDENCE_TYPES } from '../catalogs/domain-catalogs.js';
import { DEMO_COVERAGE_MUNICIPALITIES } from '../catalogs/municipalities.js';
import { MoneySchema } from '../common/money.js';
/**
 * Políticas de originación, decisión y mora (`06-backoffice.md` §4.10, valores demo en
 * `03-demo/02-datos-semilla.md` §2.1). Configurables en la consola; cambios versionados con vigencia
 * y auditoría. Las aplica código determinístico (motor de decisión / Credit Core), **nunca el LLM**.
 */
/** Ratio decimal como string (`"0.60"`, `"0.300"`), 0–1. */
const RatioSchema = z
    .string()
    .regex(/^(0(\.\d{1,6})?|1(\.0{1,6})?)$/, 'Ratio decimal entre 0 y 1')
    .meta({ description: 'Ratio decimal entre 0 y 1 como string.' });
/** Knock-outs duros (autorrechazo). */
export const KnockOutRuleSchema = z
    .enum([
    'SANCTIONS_LIST_MATCH',
    'BUREAU_CURRENT_DELINQUENCY_GT_30',
    'UNDERAGE',
    'OVER_MAX_AGE',
    'RECENT_REJECTION',
    'IDENTITY_NOT_VERIFIED',
])
    .meta({ id: 'KnockOutRule' });
/** Alertas que fuerzan revisión humana (bloquean la autoaprobación). */
export const ReviewAlertSchema = z
    .enum([
    'DOCUMENT_FRAUD_SIGNAL',
    'DOCUMENT_NEEDS_VALIDATION',
    'SANCTIONS_REVIEW',
    'SALES_TRIANGULATION_DIVERGENCE',
    'OUT_OF_COVERAGE',
    'BUREAU_THIN_FILE',
    'INCOME_INCONSISTENCY',
])
    .meta({ id: 'ReviewAlert' });
/** `decisionPolicy`: autoaprobación y autorrechazo. */
export const DecisionPolicySchema = z
    .object({
    autoApproval: z.object({
        enabled: z.boolean(),
        maxLimit: MoneySchema,
        /** Puntaje mínimo (escala 0–1000). */
        minScore: z.int().min(0).max(1000),
        /** Requiere cero alertas documentales y de listas. */
        requireNoAlerts: z.boolean(),
    }),
    autoRejection: z.object({
        enabled: z.boolean(),
        knockOuts: z.array(KnockOutRuleSchema),
    }),
    /** Alertas que fuerzan revisión humana aunque se cumpla el resto. */
    forceReviewAlerts: z.array(ReviewAlertSchema),
    /** Validez de la oferta presentada al cliente. */
    offerTtlDays: z.int().min(1).max(60),
})
    .meta({ id: 'DecisionPolicy' });
export const ScorecardFactorSchema = z.object({
    code: z.string(),
    label: z.string(),
    weight: RatioSchema,
    /** Rangos `[min, max)` → puntos; `max` null = sin tope. */
    bands: z.array(z.object({ min: z.string(), max: z.string().nullable(), points: z.int() })),
});
/** `originationPolicy`: elegibilidad, documentos, capacidad de pago y scorecard. */
export const OriginationPolicySchema = z
    .object({
    minBusinessAgeMonths: z.int().min(0),
    age: z.object({ min: z.int().min(18), max: z.int().max(100) }),
    coverage: z.object({
        /** Códigos DANE de municipios cubiertos. */
        municipalities: z.array(z.string().regex(/^\d{5}$/)),
        /** Ciudad fuera de cobertura: `HUMAN_REVIEW` (demo) o `REJECT`. */
        otherCityAction: z.enum(['HUMAN_REVIEW', 'REJECT']),
    }),
    requiredDocuments: z.object({
        /** Todos obligatorios. */
        all: z.array(EvidenceTypeSchema),
        /** Al menos `min` de esta lista. */
        anyOf: z.object({ min: z.int().min(1), types: z.array(EvidenceTypeSchema) }),
    }),
    alternativeDocuments: z.array(EvidenceTypeSchema),
    /** Overrides por tipo de negocio (código libre: `TIENDA`, `PELUQUERIA`…). */
    documentsByBusinessType: z.record(z.string(), z.array(EvidenceTypeSchema)).default({}),
    /** Cuota máxima = factor × excedente mensual. */
    paymentCapacityFactor: RatioSchema,
    /** Divergencia máxima entre fuentes de ventas; por encima → revisión humana. */
    maxSalesTriangulationDivergence: RatioSchema,
    /** Cuotas totales ≤ ratio × excedente bruto. */
    maxDebtToSurplusRatio: RatioSchema,
    reapplyAfterDays: z.int().min(0),
    knockOuts: z.array(KnockOutRuleSchema),
    scorecard: z.array(ScorecardFactorSchema),
    reminders: z.object({
        inactiveDays: z.array(z.int().min(1)),
        closeAfterDays: z.int().min(1),
        /** Ley 2300 de 2023: contactos solo en horario hábil. */
        businessHoursOnly: z.boolean(),
    }),
})
    .meta({ id: 'OriginationPolicy' });
/** `delinquencyPolicy`: bloqueo de compras, calificación y reporte a centrales. */
export const DelinquencyPolicySchema = z
    .object({
    blockPurchasesAtDpd: z.int().min(1),
    ratingBands: z.array(z.object({
        rating: z.enum(['A', 'B', 'C', 'D', 'E']),
        minDpd: z.int().min(0),
        maxDpd: z.int().min(0).nullable(),
    })),
    bureauReporting: z.object({
        /** Simulado en el demo. */
        enabled: z.boolean(),
        reportAtDpd: z.int().min(1),
        priorNoticeDays: z.int().min(0),
    }),
    chargeMoratoriumInterest: z.boolean(),
    graceDays: z.int().min(0),
})
    .meta({ id: 'DelinquencyPolicy' });
export const DEMO_DECISION_POLICY = {
    autoApproval: {
        enabled: true,
        maxLimit: { amount: '2000000.00', currency: 'COP' },
        minScore: 650,
        requireNoAlerts: true,
    },
    autoRejection: {
        enabled: true,
        knockOuts: ['SANCTIONS_LIST_MATCH', 'BUREAU_CURRENT_DELINQUENCY_GT_30', 'UNDERAGE'],
    },
    forceReviewAlerts: [
        'DOCUMENT_FRAUD_SIGNAL',
        'DOCUMENT_NEEDS_VALIDATION',
        'SANCTIONS_REVIEW',
        'SALES_TRIANGULATION_DIVERGENCE',
        'OUT_OF_COVERAGE',
    ],
    offerTtlDays: 15,
};
export const DEMO_ORIGINATION_POLICY = {
    minBusinessAgeMonths: 6,
    age: { min: 18, max: 75 },
    coverage: {
        municipalities: Object.values(DEMO_COVERAGE_MUNICIPALITIES),
        otherCityAction: 'HUMAN_REVIEW',
    },
    requiredDocuments: {
        // La cédula la valida la entidad con su proveedor: se puede subir, pero no es obligatoria.
        all: [],
        anyOf: { min: 1, types: [...SALES_EVIDENCE_TYPES] },
    },
    alternativeDocuments: [
        'UTILITY_BILL',
        'BUSINESS_PHOTO',
        'INVENTORY_PHOTO',
        'COMMERCIAL_REFERENCE',
    ],
    documentsByBusinessType: {},
    paymentCapacityFactor: '0.60',
    maxSalesTriangulationDivergence: '0.30',
    maxDebtToSurplusRatio: '0.50',
    reapplyAfterDays: 90,
    knockOuts: [
        'SANCTIONS_LIST_MATCH',
        'BUREAU_CURRENT_DELINQUENCY_GT_30',
        'UNDERAGE',
        'OVER_MAX_AGE',
        'RECENT_REJECTION',
    ],
    scorecard: [
        {
            code: 'BUREAU_SCORE',
            label: 'Puntaje en central de riesgo',
            weight: '0.40',
            bands: [
                { min: '0', max: '500', points: 200 },
                { min: '500', max: '650', points: 550 },
                { min: '650', max: '800', points: 800 },
                { min: '800', max: null, points: 1000 },
            ],
        },
        {
            code: 'PAYMENT_CAPACITY_COVERAGE',
            label: 'Cobertura de la cuota con el excedente',
            weight: '0.35',
            bands: [
                { min: '0', max: '1', points: 100 },
                { min: '1', max: '1.5', points: 600 },
                { min: '1.5', max: '2.5', points: 850 },
                { min: '2.5', max: null, points: 1000 },
            ],
        },
        {
            code: 'BUSINESS_AGE_MONTHS',
            label: 'Antigüedad del negocio (meses)',
            weight: '0.15',
            bands: [
                { min: '0', max: '6', points: 0 },
                { min: '6', max: '24', points: 600 },
                { min: '24', max: null, points: 1000 },
            ],
        },
        {
            code: 'EVIDENCE_QUALITY',
            label: 'Calidad y cantidad de soportes',
            weight: '0.10',
            bands: [
                { min: '0', max: '1', points: 300 },
                { min: '1', max: '3', points: 700 },
                { min: '3', max: null, points: 1000 },
            ],
        },
    ],
    reminders: { inactiveDays: [2, 7, 20], closeAfterDays: 30, businessHoursOnly: true },
};
export const DEMO_DELINQUENCY_POLICY = {
    blockPurchasesAtDpd: 1,
    ratingBands: [
        { rating: 'A', minDpd: 0, maxDpd: 30 },
        { rating: 'B', minDpd: 31, maxDpd: 60 },
        { rating: 'C', minDpd: 61, maxDpd: 90 },
        { rating: 'D', minDpd: 91, maxDpd: 120 },
        { rating: 'E', minDpd: 121, maxDpd: null },
    ],
    bureauReporting: { enabled: true, reportAtDpd: 30, priorNoticeDays: 20 },
    chargeMoratoriumInterest: true,
    graceDays: 0,
};
//# sourceMappingURL=policies.js.map